/**
 * InstrumentUsageService.ts
 * Service for scanning instrument usage across the trading journal system
 * 
 * Analyzes all data sources that reference instruments to determine safe delete operations.
 * Provides detailed usage information for user display and smart delete decision making.
 */

import { db } from '@/lib/db';
import type { 
  InstrumentUsageResult, 
  BulkInstrumentUsageResult, 
  UsageDetail, 
  UsageBreakdown
} from '@/types/transient/InstrumentUsage';
import { DeleteAction } from '@/types/transient/InstrumentUsage';
import type { Instrument } from '@/types/schema/Instrument';

/**
 * Service for analyzing instrument usage across all system data sources
 * 
 * This is a utility service (not extending BaseService) focused on read-only
 * analysis operations. Scans TradeExecution, MarketState, and FeeVerificationRecord
 * tables to determine instrument dependencies.
 */
export class InstrumentUsageService {
  
  // ===== SINGLE INSTRUMENT ANALYSIS =====
  
  /**
   * Analyze usage for a single instrument
   * 
   * @param instrumentId - ID of instrument to analyze
   * @returns Promise resolving to detailed usage analysis
   */
  async scanInstrumentUsage(instrumentId: string): Promise<InstrumentUsageResult> {
    try {
      // Get the instrument details for display
      const instrument = await db.instruments.get(instrumentId);
      if (!instrument) {
        throw new Error(`Instrument not found: ${instrumentId}`);
      }

      // Scan all usage sources in parallel for performance
      const [
        tradeExecutionUsage,
        marketStateUsage, 
        feeVerificationUsage
      ] = await Promise.all([
        this.scanTradeExecutionUsage(instrumentId),
        this.scanMarketStateUsage(instrumentId),
        this.scanFeeVerificationUsage(instrumentId)
      ]);

      // Calculate totals and determine action
      const totalUsageCount = 
        tradeExecutionUsage.length + 
        marketStateUsage.length + 
        feeVerificationUsage.length;

      const canHardDelete = totalUsageCount === 0;
      const recommendedAction: DeleteAction = canHardDelete ? DeleteAction.HARD_DELETE : DeleteAction.ARCHIVE;

      // Generate user-friendly explanation
      const actionReason = this.generateActionReason(
        totalUsageCount, 
        tradeExecutionUsage.length,
        marketStateUsage.length, 
        feeVerificationUsage.length
      );

      // Build usage breakdown
      const usageBreakdown: UsageBreakdown = {
        tradeExecutions: tradeExecutionUsage,
        marketStates: marketStateUsage,
        feeVerifications: feeVerificationUsage
      };

      // Get template status from existing service method
      const templateStatus = this.getInstrumentTemplateStatus(instrument);

      return {
        instrumentId,
        instrumentSymbol: instrument.symbol,
        instrumentName: instrument.displayName,
        templateStatus,
        totalUsageCount,
        usageBreakdown,
        recommendedAction,
        actionReason,
        canHardDelete,
        warnings: this.generateWarnings(instrument, totalUsageCount)
      };

    } catch (error) {
      console.error(`Failed to scan usage for instrument ${instrumentId}:`, error);
      throw new Error(`Usage scan failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ===== BULK INSTRUMENT ANALYSIS =====
  
  /**
   * Analyze usage for multiple instruments efficiently
   * 
   * @param instrumentIds - Array of instrument IDs to analyze
   * @returns Promise resolving to bulk analysis result
   */
  async scanBulkInstrumentUsage(instrumentIds: string[]): Promise<BulkInstrumentUsageResult> {
    try {
      // Scan each instrument (already optimized with parallel database queries)
      const scannedInstruments = await Promise.all(
        instrumentIds.map(id => this.scanInstrumentUsage(id))
      );

      // Calculate summary statistics
      const canHardDelete = scannedInstruments.filter(result => result.canHardDelete).length;
      const mustArchive = scannedInstruments.length - canHardDelete;
      const totalUsageReferences = scannedInstruments.reduce(
        (sum, result) => sum + result.totalUsageCount, 
        0
      );

      // Determine if this is a mixed operation
      const isMixedOperation = canHardDelete > 0 && mustArchive > 0;

      // Determine operation complexity for UX decisions
      let operationComplexity: 'simple' | 'moderate' | 'complex' = 'simple';
      if (scannedInstruments.length > 10 || totalUsageReferences > 50) {
        operationComplexity = 'complex';
      } else if (isMixedOperation || totalUsageReferences > 10) {
        operationComplexity = 'moderate';
      }

      return {
        scannedInstruments,
        summary: {
          canHardDelete,
          mustArchive,
          totalScanned: scannedInstruments.length,
          totalUsageReferences
        },
        isMixedOperation,
        operationComplexity
      };

    } catch (error) {
      console.error('Failed to scan bulk instrument usage:', error);
      throw new Error(`Bulk usage scan failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ===== USAGE SOURCE SCANNING METHODS =====

  /**
   * Scan TradeExecution table for instrument references
   * 
   * @param instrumentId - Instrument ID to search for
   * @returns Promise resolving to usage details array
   */
  private async scanTradeExecutionUsage(instrumentId: string): Promise<UsageDetail[]> {
    try {
      // Query trade executions - filter in memory since nested object queries are complex in Dexie
      const allTradeExecutions = await db.trade_executions.toArray();
      const tradeExecutions = allTradeExecutions.filter(execution => 
        execution.instrumentSnapshot?.instrumentId === instrumentId
      );

      // Convert to usage details with display information
      return tradeExecutions.map(execution => ({
        id: execution.id,
        displayText: `Trade execution: ${execution.instrumentSnapshot.symbol} (${execution.executionStatus})`,
        createdAt: execution.createdAt,
        metadata: {
          // TODO: Look up account name when account service exists
          accountName: `Account ${execution.accountId.slice(-4)}`
        }
      }));

    } catch (error) {
      console.error(`Failed to scan trade execution usage for ${instrumentId}:`, error);
      return []; // Return empty array on error to avoid breaking the analysis
    }
  }

  /**
   * Scan MarketState table for instrument references
   * 
   * @param instrumentId - Instrument ID to search for  
   * @returns Promise resolving to usage details array
   */
  private async scanMarketStateUsage(instrumentId: string): Promise<UsageDetail[]> {
    try {
      // Full table scan with filter (matches TradeExecution pattern)
      // instrumentId now lives inside instrumentSnapshot
      const marketStates = await db.market_states
        .filter(state => state.instrumentSnapshot?.instrumentId === instrumentId)
        .toArray();

      // Convert to usage details with session context
      return marketStates.map(marketState => ({
        id: marketState.id,
        displayText: `Market analysis: ${marketState.timeframe} timeframe`,
        createdAt: marketState.createdAt,
        metadata: {
          sessionDate: marketState.sessionId ? `Session ${marketState.sessionId.slice(-4)}` : 'Unknown session'
        }
      }));

    } catch (error) {
      console.error(`Failed to scan market state usage for ${instrumentId}:`, error);
      return []; // Return empty array on error to avoid breaking the analysis
    }
  }

  /**
   * Scan FeeVerificationRecord table for instrument references
   * 
   * @param instrumentId - Instrument ID to search for
   * @returns Promise resolving to usage details array  
   */
  private async scanFeeVerificationUsage(instrumentId: string): Promise<UsageDetail[]> {
    try {
      // Query fee verification records - handle optional field properly
      const feeVerifications = await db.fee_verification_records
        .filter(record => record.instrumentId === instrumentId)
        .toArray();

      // Convert to usage details with broker context
      return feeVerifications.map(verification => ({
        id: verification.id,
        displayText: `Fee verification: ${verification.commissionVerified ? 'Commission' : 'Other fees'} verified`,
        createdAt: verification.createdAt,
        metadata: {
          // TODO: Look up broker name when broker service expanded
          brokerName: `Broker ${verification.brokerId.slice(-4)}`
        }
      }));

    } catch (error) {
      console.error(`Failed to scan fee verification usage for ${instrumentId}:`, error);
      return []; // Return empty array on error to avoid breaking the analysis
    }
  }

  // ===== HELPER METHODS =====

  /**
   * Generate user-friendly explanation for recommended action
   * 
   * @param totalUsage - Total usage count across all sources
   * @param tradeCount - Number of trade execution references
   * @param marketCount - Number of market state references  
   * @param feeCount - Number of fee verification references
   * @returns Human-readable explanation string
   */
  private generateActionReason(
    totalUsage: number,
    tradeCount: number, 
    marketCount: number,
    feeCount: number
  ): string {
    if (totalUsage === 0) {
      return 'This instrument is not used anywhere in your trading journal and can be safely deleted.';
    }

    const usageParts: string[] = [];
    if (tradeCount > 0) {
      usageParts.push(`${tradeCount} trade execution${tradeCount > 1 ? 's' : ''}`);
    }
    if (marketCount > 0) {
      usageParts.push(`${marketCount} market analysis record${marketCount > 1 ? 's' : ''}`);
    }
    if (feeCount > 0) {
      usageParts.push(`${feeCount} fee verification${feeCount > 1 ? 's' : ''}`);
    }

    const usageText = usageParts.join(', ');
    return `This instrument is referenced in ${usageText}. It will be archived to preserve your trading history.`;
  }

  /**
   * Generate warning messages for user awareness
   * 
   * @param instrument - The instrument being analyzed
   * @param usageCount - Total usage count
   * @returns Array of warning messages (if any)
   */
  private generateWarnings(instrument: Instrument, usageCount: number): string[] | undefined {
    const warnings: string[] = [];

    // Warn about template sync loss
    if (instrument.instrumentReferenceId && usageCount === 0) {
      warnings.push('Deleting this instrument will lose its connection to catalog template updates.');
    }

    // Warn about custom configuration loss
    if (instrument.customizedFields && instrument.customizedFields.length > 0 && usageCount === 0) {
      warnings.push('This instrument has custom configurations that will be permanently lost.');
    }

    return warnings.length > 0 ? warnings : undefined;
  }

  /**
   * Get template status for display (reusing logic from InstrumentService)
   * 
   * @param instrument - Instrument to check
   * @returns Template status or undefined if not applicable
   */
  private getInstrumentTemplateStatus(instrument: Instrument): 'up-to-date' | 'outdated' | 'custom' | undefined {
    // Return stored template status if available
    if (instrument.templateStatus) {
      return instrument.templateStatus;
    }

    // Fallback: determine based on reference ID
    if (!instrument.instrumentReferenceId) {
      return 'custom';
    }

    // For simplicity, return stored status or undefined
    // The full template status checking logic exists in InstrumentService.getTemplateStatus()
    return undefined;
  }

  // ===== UTILITY METHODS FOR TESTING =====

  /**
   * Get usage statistics for development and testing
   * 
   * @returns Promise resolving to overall system usage statistics
   */
  async getUsageStatistics(): Promise<{
    totalTradeExecutions: number;
    totalMarketStates: number; 
    totalFeeVerifications: number;
    instrumentsWithUsage: number;
  }> {
    try {
      const [
        totalTradeExecutions,
        totalMarketStates,
        totalFeeVerifications,
        allInstruments
      ] = await Promise.all([
        db.trade_executions.count(),
        db.market_states.count(),
        db.fee_verification_records.count(),
        db.instruments.count()
      ]);

      // For now, estimate instruments with usage based on total records
      // In a real scenario, we'd need to run a more complex query
      const instrumentsWithUsage = Math.min(
        allInstruments, 
        Math.ceil((totalTradeExecutions + totalMarketStates + totalFeeVerifications) * 0.7)
      );

      return {
        totalTradeExecutions,
        totalMarketStates,
        totalFeeVerifications,
        instrumentsWithUsage
      };

    } catch (error) {
      console.error('Failed to get usage statistics:', error);
      throw new Error('Failed to retrieve usage statistics');
    }
  }
}

// Export singleton instance
export const instrumentUsageService = new InstrumentUsageService();